/*
 * Copyright (c) 2025. Sergey S. Chernov - All Rights Reserved
 *
 *  You may use, distribute and modify this code under the
 * terms of the private license, which you must obtain from the author
 *
 * To obtain the license, contact the author: https://t.me/real_sergeych or email to
 * real dot sergeych at gmail.
 */

package net.sergeych.crypto2

import kotlinx.serialization.SerialName
import kotlinx.serialization.Serializable
import kotlinx.serialization.Transient
import net.sergeych.bipack.BipackDecoder
import net.sergeych.bipack.BipackEncoder
import net.sergeych.crypto2.Container.Companion.createWith


/**
 * Multi-key encrypted container with simple adding new keys function that does not need to
 * know all existing keys, e.g., you can add recipients to the container data if you can
 * decrypt it. This is sometimes very important to be able to add recipients to the message
 * keeping existing recipients you know no keys of, or update the message when only one of the
 * keys is known.
 *
 * - [createWith] for more on create a new container
 * - [decryptWith] to decrypt
 * - [addRecipients] and various [plus] operators to add recipients
 * - [updateData] to change decrypted content for the same recipient keys
 *
 * Note that container _is serialized encrypted_.
 *
 * Some rules:
 *
 * When adding public key recipient, it is faster to use your known [DecryptingSecretKey], but you
 * can stay anonymous by just adding [EncryptingPublicKey] only.
 *
 * Put your data in [SealedBox] if you need to authenticate message origin and timestamp, then put
 * the sealed box in the [Container], this will conceal signers from attack. In the case you need to
 * prove message origin without disclosing its content, put the [Container] in the [SealedBox].
 * Everybody will see who and when have it signed, but only intended recipients could read it.
 *
 * Sample:
 * ```kotlin
 *    // create container fpr p3.public key destination using our secret key (for speed)
 *    var c = Container.createWith(data, p1.secretKey to p3.publicKey)
 *
 *    // imitation of receiving an encrypted container in binary form
 *    // so it is not decrypted
 *    c = Container.decode(c.encoded)
 *
 *    // decrypt it with the right key
 *    c.decryptWith(p3.secretKey)
 *
 *
 *    // add more keys to it: 2 symmetric, public anonymous, and secret-to-public pair (faster)
 *    c = c + symmetricKey1 + symmetricKey2 + herPublicKey + (MySecretKey to hisPublicKey)
 *
 *    // and update data in it (it will be encrypted too), adding random bytes, at least 100 and no
 *    // more than 200, to make size analysis attack almost impossible
 *    // in the 100 to 200
 *    c = c..updateData(data2, 100..22)
 *
 *    // now we can send it over the network
 *    sendOVerTheNetwork(c.encoded)
 *```
 */
@Serializable
sealed class Container {

    /**
     * Exception thrown when container inner structure is bad. It could mean it was altered.
     */
    class InvalidContainerException : Crypto2Exception("the container is invalid")

    /**
     * Attempt to decrypt container contents. This function caches decrypted data, so it is ok
     * to call it more than once.
     *
     * @param keyRing a key ring with keys that caller wants to be used
     * @return decrypted data or null if this ring contains no proper key for it
     */
    abstract fun decryptWith(keyRing: UniversalRing): UByteArray?

    /**
     * Attempt to decrypt container contents. This function caches decrypted data, so it is ok
     * to call it more than once.
     *
     * @param keys keys that caller wants to be used to decrypt with
     * @return decrypted data or null if this ring contains no proper key for it
     */
    fun decryptWith(vararg keys: DecryptingKey): UByteArray? =
        decryptWith(UniversalRing.from(*keys))

    @Transient
    var decryptedData: UByteArray? = null

    /**
     * Check the container is already decrypted. It is important, for example, to be sure it is
     * before adding more keys.
     */
    val isDecrypted: Boolean get() = decryptedData != null

    /**
     * Add one or more recipients to the __decrypted__ container using a standard builder. Note that
     * [Builder.fill] is not working in this case.
     */
    fun addRecipients(builder: Builder.() -> Unit): Container =
        if (this is Single) asOpenMulti.addRecipients(builder)
        else {
            Builder(this).apply(builder).build()
        }

    /**
     * Add e key to the __decrypted__ container. The new container is also decrypted so you can add
     * more keys, etc.
     */
    operator fun plus(recipient: EncryptingPublicKey) = addRecipients { key(recipient) }

    /**
     * Add e key to the __decrypted__ container. The new container is also decrypted so you can add
     * more keys, etc.
     */
    operator fun plus(recipient: EncryptingKey) = addRecipients { key(recipient) }

    /**
     * Add e key to the __decrypted__ container. The new container is also decrypted so you can add
     * more keys, etc.
     */
    operator fun plus(pair: Pair<DecryptingSecretKey, EncryptingPublicKey>) = addRecipients { key(pair) }

    /**
     * Update the data in the decrypted container. It keeps the same set of keys and update
     * the data only.
     */
    abstract fun updateData(newPlainData: UByteArray, randomFill: IntRange? = null): Container

    /**
     * Binary encoded _encrypted_ version. It is desirable to include [Container] as an object, though,
     * especially when using custom serialization (Json, Boss, etc.), it is serializable. Note that
     * serialized data is always encrypted.
     * Still, if you need it in binary form, this is a shortcut. You can use [decode] or call
     * [BipackDecoder.decode] to deserialize the binary form.
     */
    val encoded: UByteArray by lazy {
        BipackEncoder.encode(this).toUByteArray()
    }

    /**
     * When the container is decrypted, the [KeyId] of the key that unlocked it,
     * otherwise null.
     */
    abstract val decryptedWithKeyId: KeyId?

    /**
     * When the container is decrypted, the key that unlocked it, otherwise null.
     */
    abstract val decryptedWithKey: UniversalKey?

    /**
     * List of [KeyId] of the keys that unlocked the container, in the same order used for encryption..
     */
    abstract val keyIds: List<KeyId>


    /**
     * @suppress
     * Single-key variant, to conserve space it does not use the main key logic and just encrypts the data.
     */
    @Serializable
    @SerialName("1")
    internal class Single(
        val keyId: KeyId, val encryptedMessage: UByteArray,
        @Transient private val creationData: UByteArray? = null,
        @Transient private val reEncryptionKey: EncryptingKey? = null,
        @Transient private var encryptionPair: AsymmetricEncryptionPair? = null,
    ) : Container() {

        @Transient
        override var decryptedWithKey: UniversalKey? = null
            private set

        override val decryptedWithKeyId: KeyId?
            get() = decryptedWithKey?.id

        override val keyIds: List<KeyId> = listOf(keyId)

        init {
            decryptedData = creationData
        }

        override fun decryptWith(keyRing: UniversalRing): UByteArray? {
            decryptedData?.let { return it }
            for (k in keyRing.decryptingKeys) {
                if (k.id == keyId) {
                    kotlin.runCatching { k.decrypt(encryptedMessage) }.getOrNull()?.let {
                        decryptedData = it
                        decryptedWithKey = k as UniversalKey
                        return it
                    }
                }
            }
            return null
        }

        private fun reEncrypt(data: UByteArray? = null, f: Builder.() -> Unit): Container {
            check(isDecrypted) { "container should be decrypted" }
            return create(data ?: decryptedData ?: throw IllegalArgumentException("no data is provided")) {
                f()
                // The encryption key is known if we just created the container
                if (reEncryptionKey != null)
                    key(reEncryptionKey)
                else if (encryptionPair != null) {
                    key(encryptionPair!!)
                } else {
                    // otherwise, we don't know the encryption key and will try to derive it
                    // from the decryption key:
                    when (val k = decryptedWithKey!!) {
                        is DecryptingSecretKey -> {
                            key(k.publicKey)
                        }

                        is EncryptingKey -> {
                            key(k)
                        }

                        else -> {
                            throw IllegalStateException("unknown key type to convert container: ${k::class.simpleName}")
                        }
                    }
                }
            }

        }

        internal val asOpenMulti: Container by lazy {
            reEncrypt { alwaysMulti() }
        }

        override fun updateData(newPlainData: UByteArray, randomFill: IntRange?): Container =
            reEncrypt(newPlainData) {
                randomFill?.let { fill(it) }
            }
    }

    /**
     * @suppress
     * Implementation of 2+ keys container
     */
    @Serializable
    @SerialName("*")
    internal class Multi(
        val encryptedKeys: List<EncryptedKey>, val encryptedMessage: UByteArray,
        @Transient internal var mainKey: SymmetricKey? = null,
        @Transient internal var knownPlainData: UByteArray? = null,
    ) : Container() {
        @Serializable
        class EncryptedKey(val tag: KeyId, val cipherData: UByteArray) {
            constructor(key: EncryptingKey, encodeMainKey: UByteArray) :
                    this(key.id, key.encrypt(encodeMainKey))

            constructor(sender: DecryptingSecretKey?, recipient: EncryptingPublicKey, encodeMainKey: UByteArray) :
                    this(
                        recipient.id,
                        recipient.encryptMessage(
                            encodeMainKey,
                            senderKey = sender ?: DecryptingSecretKey.new(),
                        ).encoded
                    )
        }

        init {
            knownPlainData?.let { decryptedData = it }
        }

        @Transient
        override var decryptedWithKey: UniversalKey? = null
            private set

        override val decryptedWithKeyId: KeyId?
            get() = decryptedWithKey?.id

        override val keyIds: List<KeyId> = encryptedKeys.map { it.tag }

        override fun decryptWith(keyRing: UniversalRing): UByteArray? {
            decryptedData?.let { return it }
            for (key in keyRing.decryptingKeys) {
                val tag = key.id
                for (encryptedKey in encryptedKeys) {
                    if (tag == encryptedKey.tag) {
                        kotlin.runCatching {
                            BipackDecoder.decode<SymmetricKey>(
                                key.decrypt(encryptedKey.cipherData).toByteArray()
                            )
                        }.getOrNull()?.let { k ->
                            if (kotlin.runCatching { decryptedData = k.decrypt(encryptedMessage) }.isFailure)
                                throw InvalidContainerException()
                            decryptedWithKey = key as UniversalKey
                            mainKey = k
                        }
                        if (decryptedData != null) return decryptedData
                    }
                }
            }
            return null
        }

        override fun updateData(newPlainData: UByteArray, randomFill: IntRange?): Container =
            Builder(newPlainData, this).apply {
                randomFill?.let { fill(it) }
            }.build()
    }

    /**
     * Decrypt the container with a password. It scans all key ids for
     * these with `KDP` params, e.g., derived from password, and try to
     * derive keys from the password and decrypt the container. If there are
     * no derivable keys, or all of them failed to decrypt, returns null.
     * It could be long operation if there are multiple derivable keys with heavy
     * KDF. See [PBKD] and [KDF] for more.
     *
     * @return decrypted data or null
     */
    @Suppress("unused")
    fun decryptWithPassword(password: String): UByteArray? {
        for( id in this.keyIds ) {
            id.kdp?.let { kdp ->
                decryptWith(kdp.deriveKey(password))?.let { return it }
            }
        }
        return null
    }


    companion object {

        /**
         * The builder to create container with various parameters.
         * Use [create] to create container using a builder. Usage sample:
         *
         * ```kotlin
         * Container.create(plainData) {
         *    // optional: add a random filling from 10 to 20 bytes
         *    fill( 10 .. 20 )
         *
         *    key(symmetricKey1, symmetricKey2) // add two SymmetricKey recipients
         *
         *    key(publicKey1) // add a Asymmetric.PublicKey recipient anonymously
         *
         *    // More interesting: add publicKey2 and publicKey3 recipients using my
         *    // secret key as authority. IT is faster and allow to owner of the listed public keys
         *    // to know it was me who added them to this container.
         *    key(mySecretKey to publicKey2,mySecretKey to publicKey3)
         * }
         * ```

         */
        class Builder internal constructor(
            private val plainData: UByteArray,
            private var parent: Container? = null,
        ) {

            internal constructor(parent: Container) :
                    this(
                        parent.decryptedData ?: throw IllegalStateException("container is not decrypted"),
                        parent
                    )


            private val plainKeys = mutableListOf<EncryptingKey>()
            private val keyPairs = mutableListOf<AsymmetricEncryptionPair>()
            private var fillRange: IntRange? = null

            /**
             * Add one or more encrypting keys
             */
            fun key(vararg keys: EncryptingKey) {
                plainKeys.addAll(keys)
            }

            /**
             * Add one or more [DecryptingSecretKey] as sender authority coupled with [EncryptingPublicKey] as
             * a recipient. This is faster than anonymous usage of [EncryptingPublicKey] only
             */
            fun key(vararg pairs: AsymmetricEncryptionPair) {
                keyPairs.addAll(pairs)
            }

            /**
             * Add one or more public keys as recipients. This is slower than using pairs of sender -> recipient.
             */
            fun key(vararg publicKeys: EncryptingPublicKey) {
                keyPairs.addAll(publicKeys.map { null to it })
            }

            /**
             * Causes random filling of the encrypted message in the specified interval
             */
            @Suppress("unused")
            fun fill(range: IntRange) {
                require(range.first >= 0) { "range must be positive" }
                fillRange = range
            }

            private var alwaysMulti = false

            /**
             * @suppress
             * will produce multikey internal variant even with only one key. User internally
             */
            internal fun alwaysMulti() {
                alwaysMulti = true
            }

            /**
             * Create a Container
             */
            internal fun build(): Container {
                val countNewKeys = plainKeys.size + keyPairs.size
                if (parent != null) require(parent is Multi) { "parent container mut be a multikey variant" }
                return when {
                    countNewKeys == 0 && parent == null -> throw IllegalArgumentException("Container needs at least one key")
                    countNewKeys == 1 && alwaysMulti == false && parent == null -> {
                        createSingle()
                    }

                    else -> {
                        val eks: MutableList<Multi.EncryptedKey>
                        val mainKey: SymmetricKey
                        val p = parent
                        if (p != null) {
                            p as Multi
                            eks = p.encryptedKeys.toMutableList()
                            mainKey = p.mainKey ?: throw IllegalStateException("parent container must be decrypted")
                        } else {
                            eks = mutableListOf<Multi.EncryptedKey>()
                            mainKey = SymmetricKey.new()
                        }
                        val encodedMainKey = BipackEncoder.encode(mainKey).toUByteArray()
                        createMulti(eks, encodedMainKey, mainKey)
                    }
                }
            }

            private fun createSingle() = plainKeys.firstOrNull()?.let {
                Single(it.id, it.encrypt(plainData, fillRange), plainData, reEncryptionKey = it)
            } ?: run {
                val pair = keyPairs.first()
                val (sk, pk) = pair
                Single(
                    pk.id, pk.encryptMessage(
                        plainData,
                        senderKey = sk ?: DecryptingSecretKey.new(),
                        randomFill = fillRange
                    ).encoded,
                    plainData,
                    encryptionPair = pair
                )

            }

            private fun createMulti(
                eks: MutableList<Multi.EncryptedKey>,
                encodedMainKey: UByteArray,
                mainKey: SymmetricKey,
            ): Multi {
                for (k in plainKeys)
                    eks += Multi.EncryptedKey(k, encodedMainKey)
                for (p in keyPairs) {
                    val (sender, recipient) = p
                    eks += Multi.EncryptedKey(sender, recipient, encodedMainKey)
                }
                return Multi(eks, mainKey.encrypt(plainData, fillRange), mainKey, plainData)
            }
        }

        /**
         * Create a container using a [Builder] instance.
         * Usage sample:
         * ```kotlin
         * Container.create(plainData) {
         *    // optional: add a random filling from 10 to 20 bytes
         *    fill( 10 .. 20 )
         *
         *    key(symmetricKey1, symmetricKey2) // add two SymmetricKey recipients
         *
         *    key(publicKey1) // add a Asymmetric.PublicKey recipient anonymously
         *
         *    // More interesting: add publicKey2 and publicKey3 recipients using my
         *    // secret key as authority. IT is faster and allow to owner of the listed public keys
         *    // to know it was me who added them to this container.
         *    key(mySecretKey to publicKey2,mySecretKey to publicKey3)
         * }
         * ```
         * At least one key should be provided.
         *
         * @param plainData data to encrypt
         */
        fun create(plainData: UByteArray, builder: Builder.() -> Unit) =
            Builder(plainData).also { it.builder() }.build()


        @Suppress("unused")
        inline fun <reified T> encrypt(plainData: T, vararg keys: EncryptingKey): UByteArray =
            createWith(BipackEncoder.encode<T>(plainData).toUByteArray(), *keys).encoded

        inline fun <reified T> decrypt(cipherData: UByteArray, vararg keys: DecryptingKey): T? =
            decryptAsUBytes(cipherData, *keys)?.let { BipackDecoder.decode<T>(it.asByteArray()) }

        inline fun <reified T> decrypt(cipherData: UByteArray, ring: UniversalRing): T? =
            decode(cipherData)
                .decryptWith(ring)?.let {
                    BipackDecoder.decode<T>(it.asByteArray())
                }

        fun decryptAsUBytes(cipherData: UByteArray, vararg keys: DecryptingKey): UByteArray? =
            decode(cipherData).decryptWith(*keys)

        /**
         * Create container using one or more [EncryptingKey] and a builder, see [create]
         * for builder usage sample.
         */
        fun createWith(
            plainData: UByteArray, vararg keys: EncryptingKey,
            builder: (Builder.() -> Unit)? = null,
        ): Container =
            create(plainData) { key(*keys); builder?.invoke(this) }

        /**
         * Create the container using one or more `sender to recipient` asymmetric keys and a builder. See [create]
         * for builder usage sample.
         */
        fun createWith(plainData: UByteArray, vararg keys: AsymmetricEncryptionPair) =
            create(plainData) { key(*keys) }

        fun decode(encoded: UByteArray): Container {
            return BipackDecoder.decode(encoded.asByteArray())
        }
    }

}
//
//
//    /**
//     * General error while unpacking and decrypting the container
//     */
//    open class Error(reason: String = "failed to process the container") : IllegalArgumentException(reason)
//
//    /**
//     * Decryption failed
//     */
//    class DecryptionError : Error("failed to decrypt the container")
//
//    /**
//     * Packed container seems to be corrupted
//     */
//    class StructureError : Error("illegal container structure")
//
//    /**
//     * Key Ids for the key that can decrypt the container
//     */
//    abstract val keyIds: Set<KeyIdentity>
//    abstract fun decrypt(key: DecryptingKey): ByteArray
//
//    abstract fun update(keyRing: IdentifiableKeyring, newData: ByteArray): ByteArray?
//
//    fun decrypt(keyRing: IdentifiableKeyring): Pair<IdentifiableKey, ByteArray>? {
//        // Fast search: look for a key that shurely should
//        for (k in keyRing.byIdAndSymmetrics(keyIds)) {
//            try {
//                if( k is DecryptingKey )
//                    return k to decrypt(k)
//            } catch (x: Throwable) {
//                // This means our key is not ok. It a rare but possible collision
//            }
//        }
////        // some symmetric key can sometimes decrypt even if their ID is wrong, so
////        // we check them all:
////        for (k in keyRing.keys) {
////            if (k is SymmetricKey) {
////                try {
////                    return k to decrypt(k)
////                } catch (x: Throwable) {
////                    // it's ok. we just tried
////                }
////            }
////        }
//        return null
//    }
//
//    @Serializable
//    @SerialName("single")
//    data class Single(val keyId: KeyIdentity, val ciphertext: ByteArray) : Container() {
//        override val keyIds by lazy { setOf(keyId) }
//        override fun decrypt(key: DecryptingKey): ByteArray = try {
//            key.etaDecrypt(ciphertext)
//        } catch (x: Exception) {
//            throw DecryptionError()
//        }
//
//        override fun update(keyRing: IdentifiableKeyring, newData: ByteArray): ByteArray? {
//            return decrypt(keyRing)?.let { (k, _) ->
//                if (k !is EncryptingKey) throw IllegalArgumentException("key is not suitable for re-encryption")
//                encryptData(newData, k)
//            }
//        }
//    }
//
//    @Serializable
//    data class EncryptedKey(val id: KeyIdentity, val encryptedKey: ByteArray)
//
//    @Serializable
//    @SerialName("multi")
//    data class Multiple(val keys: List<EncryptedKey>, val ciphertext: ByteArray) : Container() {
//        override val keyIds: Set<KeyIdentity> by lazy { keys.map { it.id }.toSet() }
//
//        override fun decrypt(key: DecryptingKey): ByteArray {
//            for (k in keys) {
//                // Simple: we just use ID
//                // Complex: wrong ID with symmetric key that can decrypt:
//                if (key is SymmetricKey || k.id == key.id) {
//                    try {
//                        val dataKey = SymmetricKeys.create(key.etaDecrypt(k.encryptedKey), k.id)
//                        return dataKey.etaDecrypt(ciphertext)
//                    } catch (x: Throwable) {
//                        // might be wrong symmetric key id
//                        if( key !is SymmetricKey) throw DecryptionError()
//                    }
//                }
//            }
//            throw IllegalArgumentException("the key does not open this container")
//        }
//
//        override fun update(keyRing: IdentifiableKeyring, newData: ByteArray): ByteArray? {
//            for (k in keys) {
//                for (key in keyRing[k.id]) {
//                    if (key is DecryptingKey) {
//                        val dataKey = SymmetricKeys.create(key.etaDecrypt(k.encryptedKey), k.id)
//                        return BossEncoder.encode(Multiple(keys, dataKey.etaEncrypt(newData)) as Container)
//                    }
//                }
//            }
//            return null
//        }
//    }
//
//
////            return decrypt(keyRing)?.let { (key,_) ->
////                if(key !is EncryptingKey) throw IllegalArgumentException("key is not suitable for re-encryption")
////                if(key !is DecryptingKey) throw IllegalArgumentException("key is not suitable for re-encryption (not decrypting!)")
////                val dataKey = SymmetricKeys.create( key.etaDecrypt(k.encryptedKey), k.id)
////                encryptData(newData,k)
////            }
////        }
////    }
//
//    companion object : Loggable by LogTag("CRCON") {
//        /**
//         * Create single-key container. Most often you should call [encrypt] instead.
//         */
//        fun single(payload: ByteArray, key: EncryptingKey): ByteArray {
//            return BossEncoder.encode(Single(key.id, key.etaEncrypt(payload)) as Container)
//        }
//
//        /**
//         * Create nultiple keys container event with one key. Most often you should not call it but
//         * use [encrypt] instead.
//         */
//        fun multi(payload: ByteArray, keys: List<EncryptingKey>): ByteArray {
//            val dataKey = SymmetricKeys.random()
//            return BossEncoder.encode(
//                Multiple(
//                    keys.map { EncryptedKey(it.id, it.etaEncrypt(dataKey.packed)) },
//                    dataKey.etaEncrypt(payload)
//                ) as Container
//            )
//        }
//
//        /**
//         * Preferred method to create encrypted container. Selects the type automatically. Decrypt it with
//         * any of the provided key (their [DecryptingKey] counterparts) with [decrypt]
//         *
//         * @param payload data to encrypt. Could be `String`, `ByteArray` or any serializable type. Can't be null.
//         * @param keys one or more keys to encrypt with.
//         * @return encryped and packed container
//         */
//        inline fun <reified T> encrypt(payload: T, vararg keys: EncryptingKey): ByteArray =
//            encryptData(BossEncoder.encode(payload), *keys)
//
//        /**
//         * Encrypt payload  as is, without boss serialization. See [encrypt] for details.
//         * @return encrypted packed container
//         */
//        fun encryptData(payload: ByteArray, vararg keys: EncryptingKey): ByteArray =
//            when (keys.size) {
//                0 -> throw IllegalArgumentException("provide at least one key")
//                1 -> single(payload, keys[0])
//                else -> multi(payload, keys.toList())
//            }
//
//        /**
//         * Update packed container using proper key in the ring with a new payload. This function os useful
//         * when the container could be multiple (but it works with any) and we know only one of its keys,
//         * but want to change the data. It is possible as any of the keys could be used to re-encrypt data
//         * that _will be available to all other keys_.
//         * @param payload packed container
//         * @param keyRing keyring which should contain at least one key that can decrypt the contatiner
//         * @param packed new payload to encrypt
//         * @return packed encrypted container with new payload or null if keyring can't decrypt it
//         */
//        inline fun <reified T> update(packed: ByteArray, keyRing: IdentifiableKeyring, payload: T): ByteArray? {
//            return packed.decodeBoss<Container>().update(keyRing, BossEncoder.encode(payload))
//        }
//
//        /**
//         * Try to decrypt a container using some keys.
//         *
//         * @param keys to try to open the container.
//         * @return the decrypted paylaod on success or null if no keys could open it.
//         */
//        inline fun <reified T> decrypt(packed: ByteArray, vararg keys: DecryptingKey): T? =
//            decrypt<T>(packed, Keyring(*keys))
//
//        /**
//         * decrypt the container using leys in a keyring.
//         * @return recrypted payload or null if no key from a keyring can open it.
//         */
//        inline fun <reified T> decrypt(packed: ByteArray, ring: IdentifiableKeyring): T? {
//            return decryptAsBytes(packed, ring)?.decodeBoss<T>()
//        }
//
//        fun decryptAsBytes(packed: ByteArray, ring: IdentifiableKeyring): ByteArray? =
//            protect {
//                packed.decodeBoss<Container>().decrypt(ring)?.second
//            }
//
//        fun decryptAsBytes(packed: ByteArray, vararg keys: DecryptingKey): ByteArray? =
//            protect {
//                packed.decodeBoss<Container>().decrypt(Keyring(*keys))?.second
//            }
//
//        private inline fun <reified T> protect(f: () -> T): T =
//            try {
//                f()
//            } catch (t: Throwable) {
//                throw StructureError()
//            }
//    }
//}
